/**
 * @fileoverview Change Password tab component for the profile settings page.
 * Provides a form for users to update their password with validation.
 */

import React, { useState } from "react";
import { Grid } from "@mui/material";
import { DmsTextField, DmsButton, intl } from "dmsbasereact";
import { PasswordValidation } from "../../../components/PasswordValidation/PasswordValidation";

/**
 * Change Password Tab component
 *
 * Renders a form that allows users to change their password. The form includes:
 * - Current password field
 * - New password field
 * - Confirm password field
 * - Real-time password validation display
 *
 * @param {Object} props - Component props
 * @param {Function} props.onSubmit - Callback function to handle form submission
 * @returns {JSX.Element} The change password form tab
 */
export const ChangePasswordTab = ({ onSubmit }) => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordValid, setPasswordValid] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        setLoading(true);
        const result = await onSubmit({ current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword });
        if (result?.success) {
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        }
        setLoading(false);
    };

    return (
        <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
                <DmsTextField
                    label={intl.get("CurrentPassword").d("Current Password")}
                    type="password"
                    fullWidth
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                />
            </Grid>
            <Grid item xs={12} md={6} />
            <Grid item xs={12} md={6}>
                <DmsTextField
                    label={intl.get("NewPassword").d("New Password")}
                    type="password"
                    fullWidth
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <DmsTextField
                    label={intl.get("ConfirmPassword").d("Confirm Password")}
                    type="password"
                    fullWidth
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                />
            </Grid>
            <Grid item xs={12}>
                <PasswordValidation
                    password={newPassword}
                    confirmPassword={confirmPassword}
                    onValidChange={setPasswordValid}
                />
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <DmsButton
                    variant="success"
                    text={intl.get("Save").d("Save")}
                    onClick={handleSubmit}
                    disabled={!passwordValid || !currentPassword || loading}
                />
            </Grid>
        </Grid>
    );
};
